/**
 * Map between the contract's display `billingCurrency` (a UI symbol) and the
 * ISO lower-case code Stripe uses on every Price / line item.
 *
 * Mirrors `symbolToStripeCurrency` on the backend (xeroapi). Keep these in sync.
 */

/** Supported `billingCurrency` values on POST /my/agreements. */
const BILLING_CURRENCY_OPTIONS = ['£', '$', '€', 'AUD'];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * @param {string} symbol — `£` `$` `€` `AUD` (or already-ISO codes — passed through).
 * @returns {string|null} ISO 4217 lower-case (`gbp`, `usd`, `eur`, `aud`) or `null` if unsupported.
 */
const symbolToStripeCurrency = (symbol) => {
  if (!symbol) return null;
  const s = String(symbol).trim();

  if (s === '£') return 'gbp';
  if (s === '$') return 'usd';
  if (s === '€') return 'eur';
  if (s === 'AUD' || s === 'A$' || s === 'AU$') return 'aud';

  // Pass-through for ISO codes already supplied by callers (case-insensitive).
  const iso = s.toUpperCase().replace(/[^A-Z]/g, '');
  if (iso.length === 3) return iso.toLowerCase();

  return null;
};

/**
 * Inverse of {@link symbolToStripeCurrency} for label rendering.
 * @param {string} code — ISO lower-case.
 * @returns {string} Display symbol (falls back to the upper-case code).
 */
const stripeCurrencyToSymbol = (code) => {
  if (!code) return '';
  const c = String(code).toLowerCase();
  if (c === 'gbp') return '£';
  if (c === 'usd') return '$';
  if (c === 'eur') return '€';
  if (c === 'aud') return 'A$';
  return c.toUpperCase();
};

/**
 * Normalise a display symbol for the agreements API (`A$` → `AUD`).
 * @param {string} symbol
 * @returns {string|null}
 */
const normalizeBillingSymbol = (symbol) => {
  if (!symbol) return null;
  const s = String(symbol).trim();
  if (s === 'A$' || s === 'AU$') return 'AUD';
  if (BILLING_CURRENCY_OPTIONS.includes(s)) return s;
  const iso = symbolToStripeCurrency(s);
  if (!iso) return null;
  if (iso === 'aud') return 'AUD';
  const fromIso = stripeCurrencyToSymbol(iso);
  return fromIso === 'A$' ? 'AUD' : fromIso;
};

/**
 * Resolve organisation `currency` (UUID, legacy symbol, or ISO) to a contract
 * `billingCurrency` symbol. Returns `null` when the value cannot be mapped.
 *
 * @param {string} value — `org.currency` from DynamoDB or a symbol typed by an admin.
 * @param {{ id: string, symbol?: string, isoCode?: string }[]} [currencies]
 * @returns {string|null}
 */
const resolveBillingCurrencySymbol = (value, currencies = []) => {
  if (!value) return null;
  const s = String(value).trim();

  const direct = normalizeBillingSymbol(s);
  if (direct) return direct;

  if (UUID_RE.test(s)) {
    const row = currencies.find((c) => c.id === s);
    if (!row) return null;
    const fromSymbol = normalizeBillingSymbol(row.symbol);
    if (fromSymbol) return fromSymbol;
    if (row.isoCode) return normalizeBillingSymbol(row.isoCode);
    return null;
  }

  return null;
};

/**
 * Human-readable org currency for admin hints (never shows a raw UUID).
 * @param {string} value
 * @param {{ id: string, symbol?: string, isoCode?: string, name?: string }[]} [currencies]
 * @returns {string}
 */
const formatOrganisationCurrency = (value, currencies = []) => {
  if (!value) return '';
  const billing = resolveBillingCurrencySymbol(value, currencies);
  if (billing) return billing;
  if (UUID_RE.test(String(value))) return 'unknown currency';
  return String(value);
};

/**
 * Convert an admin-entered amount in major units (`70`, `19.99`) to the minor
 * units every Stripe Price expects (`7000`, `1999`).
 *
 * @param {number|string} amount
 * @returns {number|undefined} `undefined` when the input is blank or not numeric.
 */
const majorToMinorUnits = (amount) => {
  if (amount === '' || amount == null) return undefined;
  const major = Number(amount);
  if (!Number.isFinite(major)) return undefined;
  return Math.round(major * 100);
};

/**
 * Inverse of {@link majorToMinorUnits}, for pre-filling admin forms from stored
 * Stripe amounts.
 *
 * @param {number|string} amount — minor units.
 * @returns {number|string} Major units, or `''` when the input is blank.
 */
const minorToMajorUnits = (amount) => {
  if (amount === '' || amount == null) return '';
  const minor = Number(amount);
  if (!Number.isFinite(minor)) return '';
  return minor / 100;
};

/**
 * Convert a Stripe `recurring` block to our contract's `billingFrequency` string.
 *
 * The contract uses friendly tokens (`monthly`, `quarterly`, `annual`) that the
 * indexation/provisioner pipeline understands. Anything we don't recognise is
 * returned as-is so admins can override.
 *
 * @param {{ interval?: string, interval_count?: number }} [recurring]
 * @returns {string|null}
 */
const recurringToBillingFrequency = (recurring) => {
  if (!recurring) return null;
  const interval = recurring.interval;
  const count = recurring.interval_count || 1;

  if (interval === 'year' && count === 1) return 'annual';
  if (interval === 'month' && count === 12) return 'annual';
  if (interval === 'month' && count === 1) return 'monthly';
  if (interval === 'month' && count === 3) return 'quarterly';
  if (interval === 'week' && count === 1) return 'weekly';
  if (interval === 'day' && count === 1) return 'daily';

  return `${count} ${interval}`;
};

export {
  BILLING_CURRENCY_OPTIONS,
  symbolToStripeCurrency,
  stripeCurrencyToSymbol,
  majorToMinorUnits,
  minorToMajorUnits,
  recurringToBillingFrequency,
  resolveBillingCurrencySymbol,
  formatOrganisationCurrency,
  normalizeBillingSymbol,
};
